import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { guidesApi, Guide, ApiRequestError } from '@/lib/api';

export default function GuideListPage() {
  const [guides, setGuides] = useState<Guide[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [draggedIndex, setDraggedIndex] = useState<number | null>(null);
  const [isReordering, setIsReordering] = useState(false);

  useEffect(() => {
    loadGuides();
  }, []);

  const loadGuides = async () => {
    setIsLoading(true);
    try {
      const result = await guidesApi.list();
      setGuides(result.guides);
    } catch (err) {
      console.error('Failed to load guides:', err);
      setError('Failed to load guides');
    } finally {
      setIsLoading(false);
    }
  };

  const handleDragStart = (index: number) => {
    setDraggedIndex(index);
  };

  const handleDragOver = (e: React.DragEvent, index: number) => {
    e.preventDefault();
    if (draggedIndex === null || draggedIndex === index) return;

    const newGuides = [...guides];
    const draggedItem = newGuides[draggedIndex];
    newGuides.splice(draggedIndex, 1);
    newGuides.splice(index, 0, draggedItem);
    setGuides(newGuides);
    setDraggedIndex(index);
  };

  const handleDragEnd = async () => {
    if (draggedIndex === null) return;
    setDraggedIndex(null);

    // Save new order
    setIsReordering(true);
    try {
      const order = guides.map((guide, index) => ({
        id: guide.id,
        sortOrder: index,
      }));
      await guidesApi.reorder(order);
    } catch (err) {
      console.error('Failed to reorder guides:', err);
      // Reload to restore original order
      loadGuides();
    } finally {
      setIsReordering(false);
    }
  };

  const handleDelete = async (guide: Guide) => {
    if (
      !confirm(
        `Are you sure you want to delete "${guide.title}"? This action cannot be undone.`
      )
    ) {
      return;
    }

    try {
      await guidesApi.delete(guide.id);
      setGuides(guides.filter((g) => g.id !== guide.id));
    } catch (err) {
      if (err instanceof ApiRequestError) {
        alert(err.message);
      } else {
        alert('Failed to delete guide');
      }
    }
  };

  const handleTogglePublish = async (guide: Guide) => {
    try {
      await guidesApi.update(guide.id, { isPublished: !guide.isPublished });
      setGuides(
        guides.map((g) =>
          g.id === guide.id ? { ...g, isPublished: !g.isPublished } : g
        )
      );
    } catch (err) {
      if (err instanceof ApiRequestError) {
        alert(err.message);
      } else {
        alert('Failed to update guide');
      }
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Manage Guides</h1>
          <p className="mt-1 text-sm text-gray-500">
            Create and organize guide pages for apartment information.
          </p>
        </div>
        <Link to="/admin/guides/new" className="btn-primary">
          <svg
            className="w-4 h-4 mr-2"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M12 4v16m8-8H4"
            />
          </svg>
          New Guide
        </Link>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-md">
          {error}
        </div>
      )}

      <div className="card">
        {isLoading ? (
          <div className="flex justify-center py-12">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
          </div>
        ) : guides.length === 0 ? (
          <div className="text-center py-12">
            <svg
              className="mx-auto h-12 w-12 text-gray-400"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253"
              />
            </svg>
            <h3 className="mt-4 text-lg font-medium text-gray-900">
              No Guides Yet
            </h3>
            <p className="mt-2 text-sm text-gray-500">
              Create your first guide page to help guests.
            </p>
            <Link to="/admin/guides/new" className="btn-primary mt-4">
              Create Guide
            </Link>
          </div>
        ) : (
          <div className="divide-y divide-gray-200">
            <div className="px-4 py-3 bg-gray-50 text-xs font-medium text-gray-500 uppercase tracking-wider">
              <div className="flex items-center">
                <div className="w-8"></div>
                <div className="flex-1">Title</div>
                <div className="w-24 text-center">Status</div>
                <div className="w-24 text-center">Access</div>
                <div className="w-32 text-right">Actions</div>
              </div>
            </div>
            {guides.map((guide, index) => (
              <div
                key={guide.id}
                draggable
                onDragStart={() => handleDragStart(index)}
                onDragOver={(e) => handleDragOver(e, index)}
                onDragEnd={handleDragEnd}
                className={`px-4 py-4 flex items-center ${
                  draggedIndex === index ? 'bg-primary-50' : 'hover:bg-gray-50'
                } ${isReordering ? 'opacity-50' : ''}`}
              >
                <div className="w-8 cursor-grab">
                  <svg
                    className="w-5 h-5 text-gray-400"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M4 8h16M4 16h16"
                    />
                  </svg>
                </div>
                <div className="flex-1">
                  <Link
                    to={`/admin/guides/${guide.id}/edit`}
                    className="font-medium text-gray-900 hover:text-primary-600"
                  >
                    {guide.title}
                  </Link>
                  <div className="text-sm text-gray-500">/guide/{guide.slug}</div>
                </div>
                <div className="w-24 text-center">
                  <button
                    onClick={() => handleTogglePublish(guide)}
                    className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                      guide.isPublished
                        ? 'bg-green-100 text-green-800'
                        : 'bg-gray-100 text-gray-800'
                    }`}
                  >
                    {guide.isPublished ? 'Published' : 'Draft'}
                  </button>
                </div>
                <div className="w-24 text-center">
                  {guide.requiresBooking ? (
                    <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-amber-100 text-amber-800">
                      Booking
                    </span>
                  ) : (
                    <span className="text-sm text-gray-500">Public</span>
                  )}
                </div>
                <div className="w-32 text-right space-x-2">
                  <Link
                    to={`/guide/${guide.slug}`}
                    className="text-gray-400 hover:text-gray-600"
                    title="View"
                  >
                    <svg
                      className="w-5 h-5 inline"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
                      />
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"
                      />
                    </svg>
                  </Link>
                  <Link
                    to={`/admin/guides/${guide.id}/edit`}
                    className="text-gray-400 hover:text-primary-600"
                    title="Edit"
                  >
                    <svg
                      className="w-5 h-5 inline"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"
                      />
                    </svg>
                  </Link>
                  <button
                    onClick={() => handleDelete(guide)}
                    className="text-gray-400 hover:text-red-600"
                    title="Delete"
                  >
                    <svg
                      className="w-5 h-5 inline"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                      />
                    </svg>
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      <div className="text-sm text-gray-500">
        <strong>Tip:</strong> Drag guides to reorder them. The order will be
        reflected in the guide navigation.
      </div>
    </div>
  );
}
